/**
 * 事件详情数据 Hook
 * 用于获取和处理 /overview/anomaly_detail 接口数据
 * 支持模块级内存缓存，相同参数的请求直接返回缓存数据
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  getEventDetail,
  type IEventDetailListResponse,
} from '@op/services/keyMetrics';
import { regionCn } from '@op/constants';
import type { IEventDetailWithRegion, IDetailQueryParams } from '../types.d';

// ========== 缓存管理 ==========

/** 模块级内存缓存：已完成的请求结果 */
const cacheMap = new Map<string, IEventDetailWithRegion[]>();

/** 模块级在途请求 Map：防止相同参数并发请求。value 是正在进行的请求 Promise */
const pendingMap = new Map<string, Promise<IEventDetailWithRegion[]>>();

/**
 * 根据请求参数生成缓存 key
 */
function getCacheKey(params: Pick<IDetailQueryParams, 'edition_unified_id' | 'start_time' | 'end_time' | 'data_type'>): string {
  const { edition_unified_id, start_time, end_time, data_type } = params;
  return `${edition_unified_id}|${start_time}|${end_time}|${data_type}`;
}

// ========== 类型定义 ==========

export interface IUseEventDetailResult {
  eventDetails: IEventDetailWithRegion[];
  activeRegion: string | null;
  setActiveRegion: (region: string) => void;
  loading?: boolean;
  error: boolean;
  /** silent: true → 只写缓存，更新 hover 数据（用于 hover 预热） */
  fetchData: (params: IDetailQueryParams, options?: { silent?: boolean }) => Promise<void>;
  empty?: boolean;
  hasMultipleRegions: boolean;
  /** hover 预览数据（silent 模式写入，不污染 eventDetails） */
  hoverEventDetails: IEventDetailWithRegion[];
  hoverLoading: boolean;
}

// ========== 数据处理函数 ==========

/**
 * 从接口响应转换为事件详情数据数组
 */
function transformEventDetailList(
  data: IEventDetailListResponse | null | undefined,
): IEventDetailWithRegion[] {
  if (!data?.list) return [];

  return Object.entries(data.list).map(([regionKey, item]) => {
    const realRegionKey =
      regionKey.toLocaleLowerCase() === 'all' ? 'Global' : regionKey;
    return {
      regionKey: realRegionKey,
      startDate: item.detail.start_date ?? '',
      endDate: item.detail.end_date ?? '',
      region: regionCn[realRegionKey] || realRegionKey,
      title: item.detail.overview_title ?? '',
      overview: item.detail.overview ?? '',
      contribution: (item.detail.overview_contribution ?? 0) * 100,
      trend: item.detail.overview_trend ?? '',
      factors:
        item.detail.factors?.map(f => ({
          name: f.name,
          contribution: (f.contribution ?? 0) * 100,
          trend: f.trend,
          detail: f.detail,
          comment_list: f.comment_list || [],
        })) ?? [],
      links: item.detail.links ?? [],
      remark: item.detail.remark ?? '',
    };
  });
}

// ========== Hook ==========

export const useEventDetail = (): IUseEventDetailResult => {
  const [empty, setEmpty] = useState(false);
  const [eventDetails, setEventDetails] = useState<IEventDetailWithRegion[]>(
    [],
  );
  const [activeRegion, setActiveRegion] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  /** hover 预览数据：与 eventDetails 独立，hover 不污染侧边栏 */
  const [hoverEventDetails, setHoverEventDetails] = useState<IEventDetailWithRegion[]>([]);
  const [hoverLoading, setHoverLoading] = useState(false);

  /** 请求序列号：解决快速切换弹窗时的竞态问题，只应用最新请求的结果 */
  const latestRequestRef = useRef(0);

  /** 缓存 activeRegion 最新值，供 applyEventDetails 在回调闭包中读取 */
  const activeRegionRef = useRef<string | null>(null);
  activeRegionRef.current = activeRegion;

  const fetchData = useCallback(
    async (params: IDetailQueryParams, options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      const { edition_unified_id, start_time, end_time, data_type } =
        params || {};
      if (!edition_unified_id || !start_time || !end_time || !data_type) {
        if (!silent) {
          setEventDetails([]);
          setActiveRegion(null);
          setEmpty(true);
        }
        return;
      }

      // 分配本次请求的序列号，用于竞态判断
      const requestId = ++latestRequestRef.current;

      const cacheKey = getCacheKey(params);

      // 1. 已完成缓存命中
      const cached = cacheMap.get(cacheKey);
      if (cached) {
        if (requestId === latestRequestRef.current) {
          if (silent) {
            setHoverEventDetails(cached);
            setHoverLoading(false);
          } else {
            setLoading(false);
            setError(false);
            applyEventDetails(cached, params);
          }
        }
        return;
      }

      // 2. 在途请求去重
      const pending = pendingMap.get(cacheKey);
      if (pending) {
        if (silent) {
          setHoverLoading(true);
        }
        try {
          const transformed = await pending;
          if (requestId === latestRequestRef.current) {
            if (silent) {
              setHoverEventDetails(transformed);
              setHoverLoading(false);
            } else {
              setLoading(false);
              setError(false);
              applyEventDetails(transformed, params);
            }
          }
        } catch (err) {
          console.error('获取事件详情失败:', err);
          if (requestId === latestRequestRef.current) {
            if (silent) {
              setHoverEventDetails([]);
              setHoverLoading(false);
            } else {
              setEventDetails([]);
              setActiveRegion(null);
              setEmpty(true);
              setLoading(false);
              setError(true);
            }
          }
        }
        return;
      }

      // 3. 发起新请求（不同参数各自独立请求，互不取消）
      if (requestId === latestRequestRef.current) {
        if (silent) {
          setHoverLoading(true);
        } else {
          setEventDetails([]);
          // 不在此处重置 activeRegion，避免覆盖上层调用方（handleEventClickWithTracking）
          // 已经设置的正确 tab 值。applyEventDetails 会在 API 返回后统一设置正确的 activeRegion。
          setEmpty(false);
          setLoading(true);
          setError(false);
        }
      }

      // 创建请求 Promise 并存入 pendingMap，用于后续相同参数的去重
      const requestPromise = (async () => {
        const res = await getEventDetail({data: params as any});
        return transformEventDetailList(res);
      })();

      pendingMap.set(cacheKey, requestPromise);

      try {
        const transformed = await requestPromise;

        // 写入已完成缓存（缓存不受竞态/silent 影响，始终写入）
        cacheMap.set(cacheKey, transformed);

        if (requestId === latestRequestRef.current) {
          if (silent) {
            setHoverEventDetails(transformed);
            setHoverLoading(false);
          } else {
            applyEventDetails(transformed, params);
            setLoading(false);
            setError(false);
          }
        }
      } catch (err) {
        console.error('获取事件详情失败:', err);
        if (requestId === latestRequestRef.current) {
          if (silent) {
            setHoverEventDetails([]);
            setHoverLoading(false);
          } else {
            setEventDetails([]);
            setActiveRegion(null);
            setEmpty(true);
            setLoading(false);
            setError(true);
          }
        }
      } finally {
        pendingMap.delete(cacheKey);
      }
    },
    [],
  );

  /**
   * 应用事件详情数据并设置默认 activeRegion
   * - 若当前 activeRegion 已存在于返回数据中，保留不动（不覆盖用户已选的 tab）
   * - 否则按 params.region 匹配，匹配不到则选第一个
   */
  const applyEventDetails = useCallback(
    (details: IEventDetailWithRegion[], params: IDetailQueryParams) => {
      if (details.length > 0) {
        const currentRegion = activeRegionRef.current;
        // 当前选中的 tab 在返回数据中存在，保留不覆盖
        if (
          currentRegion &&
          details.some(item => item.regionKey === currentRegion)
        ) {
          setEventDetails(details);
          setEmpty(false);
          return;
        }
        // 否则按参数匹配或默认选第一个
        const { region } = params || {};
        const matchedRegion =
          details?.find(item => item.regionKey === region) || details[0];
        setEventDetails(details);
        setActiveRegion(matchedRegion.regionKey);
        setEmpty(false);
      } else {
        setEventDetails([]);
        setActiveRegion(null);
        setEmpty(true);
      }
    },
    [],
  );

  // 离开页面时清除缓存
  useEffect(() => {
    return () => {
      cacheMap.clear();
      pendingMap.clear();
    };
  }, []);

  const hasMultipleRegions = eventDetails.length > 1;
  return {
    eventDetails,
    activeRegion,
    setActiveRegion,
    loading,
    error,
    fetchData,
    empty,
    hasMultipleRegions,
    hoverEventDetails,
    hoverLoading,
  };
};

export default useEventDetail;
